import { useCallback } from 'react'
import { GridApi } from 'ag-grid-community'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import {
  CriticalPathLinkData,
  CriticalPathDragContextValue,
  useCriticalPathContext,
} from './CriticalPathContext'
import { useCriticalPathDragInteraction } from './useCriticalPathDragInteraction'
import {
  CpPopupState,
  useCriticalPathLinePopup,
} from './useCriticalPathLinePopup'
import { DatePosition } from '../hooks/useDatePositions'
import { categorizeLinks, FallbackLinkInfo } from '../utils/visibilityFilters'
import {
  getWbsItemDisplayName,
  getWbsItemIconUrl,
  getWbsItemScheduledDate,
} from '../utils/linkInfoPopupUtils'
import { LinkEditorResult } from '../WbsItemLink/LinkEditorPopup'

type UseGanttCriticalPathInteractionParams = {
  api: GridApi
  dataPath?: string
  rowIndex: number
  isProcess: boolean
  wbsItemUuid: string | null
  barEndpoints: { startX: number | null; endX: number | null }
  datePositions: DatePosition[]
  isHovered: boolean
  isDependencyDragging: boolean
}

type UseGanttCriticalPathInteractionResult = {
  criticalPathLinks: CriticalPathLinkData[]
  fallbackCPs: FallbackLinkInfo<CriticalPathLinkData>[]
  isCriticalPathDragging: boolean
  isCriticalPathDropTarget: boolean
  showCriticalPathDropHighlight: boolean
  showCriticalPathPreDragCircle: boolean
  criticalPathPreDragInfo: {
    date: CalendarDateVO
    pixelX: number
    position: 'top' | 'bottom'
  } | null
  isCriticalPathRowHovered: boolean
  setIsCriticalPathRowHovered: (v: boolean) => void
  criticalPathDropPointPercent: number | null
  criticalPathDragContext: CriticalPathDragContextValue | null
  handleCriticalPathDropMouseUp: (event: React.MouseEvent) => void
  handleCriticalPathDropRowMouseMove: (e: React.MouseEvent) => void
  cpPopup: CpPopupState | null
  handleCriticalPathLineClick: (
    link: CriticalPathLinkData,
    event: React.MouseEvent
  ) => void
  handleDeleteCriticalPath: () => void
  handleCriticalPathUpdate: (result: LinkEditorResult) => void
  closeCriticalPathPopup: () => void
  handleFallbackCpClick: (
    info: FallbackLinkInfo<CriticalPathLinkData>,
    event: React.MouseEvent
  ) => void
  handleCellMouseMove: (
    clientX: number,
    clientY: number,
    containerRect: DOMRect
  ) => void
  handleCellMouseDown: (
    e: MouseEvent,
    containerRect: DOMRect,
    processBarHalfHeight: number,
    circleSize: number
  ) => void
}

export const useGanttCriticalPathInteraction = (
  params: UseGanttCriticalPathInteractionParams
): UseGanttCriticalPathInteractionResult => {
  const {
    api,
    dataPath,
    rowIndex,
    isProcess,
    wbsItemUuid,
    barEndpoints,
    datePositions,
    isHovered,
    isDependencyDragging,
  } = params
  const prefix = dataPath ? `${dataPath}.` : ''

  // Critical path context: categorize into visible lines and fallback icons
  const criticalPathContext = useCriticalPathContext()
  const { visible: criticalPathLinks, fallback: fallbackCPs } = categorizeLinks(
    criticalPathContext?.criticalPathLinks ?? [],
    api
  )

  // Critical path drag interaction
  const {
    isCriticalPathDragging,
    isCriticalPathDropTarget,
    showCriticalPathDropHighlight,
    showPreDragCircle: showCriticalPathPreDragCircle,
    preDragInfo: criticalPathPreDragInfo,
    isCriticalPathRowHovered,
    setIsCriticalPathRowHovered,
    handleBarMouseMove: handleCriticalPathBarMouseMove,
    handleDropMouseUp: handleCriticalPathDropMouseUp,
    handleDropRowMouseMove: handleCriticalPathDropRowMouseMove,
    dropPointPercent: criticalPathDropPointPercent,
    criticalPathDragContext,
  } = useCriticalPathDragInteraction({
    rowIndex,
    isProcess,
    wbsItemUuid,
    barEndpoints,
    datePositions,
    isHovered,
    isDependencyDragging,
  })

  // Critical path line popup
  const {
    cpPopup,
    handleLineClick: handleCriticalPathLineClick,
    handleDelete: handleDeleteCriticalPath,
    handleUpdate: handleCriticalPathUpdate,
    closePopup: closeCriticalPathPopup,
    openPopup: openCpPopup,
  } = useCriticalPathLinePopup(api, dataPath)

  // WBS item info map from context (server-sourced, avoids full-grid iteration)
  const cpWbsItemInfoMap = criticalPathContext?.wbsItemInfoMap

  // Fallback icon click handler (resolve names/icons for hidden endpoints)
  const handleFallbackCpClick = useCallback(
    (info: FallbackLinkInfo<CriticalPathLinkData>, event: React.MouseEvent) => {
      const { link, from, to } = info
      let fromName: string
      let fromIconUrl: string | undefined
      let fromScheduled: { startDate?: string; endDate?: string } | undefined
      if (from.isFallback) {
        const resolved = cpWbsItemInfoMap?.get(link.fromWbsItemUuid)
        fromName = resolved?.displayName ?? ''
        fromIconUrl = resolved?.iconUrl
      } else {
        fromName = getWbsItemDisplayName(api, from.rowIndex, prefix)
        fromIconUrl = getWbsItemIconUrl(api, from.rowIndex, prefix)
        fromScheduled = getWbsItemScheduledDate(api, from.rowIndex, prefix)
      }
      let toName: string
      let toIconUrl: string | undefined
      let toScheduled: { startDate?: string; endDate?: string } | undefined
      if (to.isFallback) {
        const resolved = cpWbsItemInfoMap?.get(link.toWbsItemUuid)
        toName = resolved?.displayName ?? ''
        toIconUrl = resolved?.iconUrl
      } else {
        toName = getWbsItemDisplayName(api, to.rowIndex, prefix)
        toIconUrl = getWbsItemIconUrl(api, to.rowIndex, prefix)
        toScheduled = getWbsItemScheduledDate(api, to.rowIndex, prefix)
      }
      const toDateRangeFn = (
        s: { startDate?: string; endDate?: string } | undefined
      ): { min: Date; max: Date } | null => {
        if (!s?.startDate || !s?.endDate) return null
        return { min: new Date(s.startDate), max: new Date(s.endDate) }
      }
      openCpPopup({
        link,
        fromName,
        toName,
        fromIconUrl,
        toIconUrl,
        fromDateRange: toDateRangeFn(fromScheduled),
        toDateRange: toDateRangeFn(toScheduled),
      })
    },
    [api, prefix, openCpPopup, cpWbsItemInfoMap]
  )

  // Cell-level mousemove handler for critical path pre-drag tracking.
  // Called from native DOM event listener delegated at the cell level.
  const handleCellMouseMove = useCallback(
    (clientX: number, clientY: number, containerRect: DOMRect) => {
      if (!isProcess || isCriticalPathDragging || isDependencyDragging) return
      handleCriticalPathBarMouseMove(clientX, clientY, containerRect)
    },
    [
      isProcess,
      isCriticalPathDragging,
      isDependencyDragging,
      handleCriticalPathBarMouseMove,
    ]
  )

  // Cell-level mousedown handler for critical path drag start via indicator icon.
  // The indicator icon is rendered with pointer-events: none to avoid blocking
  // the MUI Slider. Instead, we detect clicks on the icon position at the cell level.
  const handleCellMouseDown = useCallback(
    (
      e: MouseEvent,
      containerRect: DOMRect,
      processBarHalfHeight: number,
      circleSize: number
    ) => {
      if (
        !isProcess ||
        !criticalPathPreDragInfo ||
        isCriticalPathDragging ||
        isDependencyDragging ||
        !criticalPathDragContext ||
        !wbsItemUuid ||
        rowIndex < 0
      ) {
        return
      }
      const { pixelX, position, date } = criticalPathPreDragInfo
      const iconCenterX = containerRect.left + pixelX
      const isTop = position === 'top'
      const iconCenterY = isTop
        ? containerRect.top + containerRect.height * 0.5 - processBarHalfHeight
        : containerRect.top + containerRect.height * 0.5 + processBarHalfHeight
      const halfSize = circleSize / 2
      const isWithinIcon =
        e.clientX >= iconCenterX - halfSize &&
        e.clientX <= iconCenterX + halfSize &&
        e.clientY >= iconCenterY - halfSize &&
        e.clientY <= iconCenterY + halfSize
      if (isWithinIcon) {
        e.preventDefault()
        e.stopPropagation()
        criticalPathDragContext.startDrag({
          fromRowIndex: rowIndex,
          fromDate: date,
          startX: e.clientX,
          startY: e.clientY,
        })
      }
    },
    [
      isProcess,
      criticalPathPreDragInfo,
      isCriticalPathDragging,
      isDependencyDragging,
      criticalPathDragContext,
      wbsItemUuid,
      rowIndex,
    ]
  )

  return {
    criticalPathLinks,
    fallbackCPs,
    isCriticalPathDragging,
    isCriticalPathDropTarget,
    showCriticalPathDropHighlight,
    showCriticalPathPreDragCircle,
    criticalPathPreDragInfo,
    isCriticalPathRowHovered,
    setIsCriticalPathRowHovered,
    criticalPathDropPointPercent,
    criticalPathDragContext,
    handleCriticalPathDropMouseUp,
    handleCriticalPathDropRowMouseMove,
    cpPopup,
    handleCriticalPathLineClick,
    handleDeleteCriticalPath,
    handleCriticalPathUpdate,
    closeCriticalPathPopup,
    handleFallbackCpClick,
    handleCellMouseMove,
    handleCellMouseDown,
  }
}
